import { Flex, Text, Image, Box } from 'rebass';
import { height } from 'styled-system';
import { FiImage, FiEdit } from "react-icons/fi";
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import posed from 'react-pose';
import styled from '@emotion/styled';
import defaultPhotoPreview from '../../../../stories/assets/Default_PreviewPhoto-Rounded.png'
import * as loadImage from 'blueimp-load-image';
import useEntity from 'entities/hooks/use-entity';
import AvatarEditor from 'react-avatar-editor';

const PosedBox = posed.div({
  loading: {
    opacity: 0.5,
  },
});

const AvatarPreviewWrap = styled(PosedBox)`
  overflow: hidden;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin:auto;
  img {
    max-height: 100%;
    max-width: 100%;
    ${height};
    border-radius: 10px;
    object-fit: cover;
    object-position: center;
  }

  svg {
    opacity: 0.75;
  }

  p {
    text-align: center;
    margin: 0 8px;
  }
`;

const EditButton = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  height: 36px;
  width: 36px;
  border-radius: 50%;
  background: ${props => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: ${props => props.theme.colors.mutedText};
  z-index: 1;
`;

const AvatarPreviewImage = ({ src, circular, canvasId, iconSize, onEditPress, hasEditButton, showCroppingParams, ...props }) => {
  const { entity } = useEntity({ type: 'images', id: canvasId });
  const canvasImage = entity.tmpUrl ||
    (entity['image-versions'] &&
      entity['image-versions'][3].url) ||
    entity['asset-url']

  const [imgSrc, setImgSrc] = useState(src);
  const [imageBlob, setImageBlob] = useState(null);
  const mounted = useRef(false);

  useEffect(() => {
    if (src instanceof File || src instanceof Blob) {
      if (!mounted.current) {
        setImgSrc(URL.createObjectURL(src));
      }
    }

    mounted.current = true;
    return () => {
      if (src instanceof File || src instanceof Blob) {
        URL.revokeObjectURL(imgSrc);
      }
    };
  }, [imgSrc, src]);

  function RenderImage() {
    if (imgSrc) {
      if (showCroppingParams)
        return <Box
          width={showCroppingParams.width}
          height={showCroppingParams.height}
          sx={{ borderRadius: '50%', position: 'relative' }}
        >
          <AvatarEditor
            image={imgSrc}
            width={showCroppingParams.width}
            height={showCroppingParams.height}
            borderRadius={showCroppingParams.borderRadius}
            color={[250, 250, 250, 1]}
            scale={showCroppingParams.zoom}
            position={showCroppingParams.position}
            style={{
              minWidth: "100%",
              width: "100%",
              height: "100%",
              objectFit: 'cover',
            }}
          />
        </Box>
      else
        return <img crossOrigin="anonymous" src={canvasImage || imgSrc} alt="Preview" style={{ maxWidth: '100%', borderRadius: '50%' }}
          onLoad={async () => {
            if (!imgSrc.includes('blob:')) {
              const processedFile = await new Promise(resolve => {
                loadImage(
                  imgSrc,
                  canvas => {
                    canvas.toBlob(
                      blob => {
                        const nextFile = blob;
                        nextFile.lastModifiedAt = new Date();
                        nextFile.name = 'avatar.jpg';
                        resolve(blob);
                      },
                      'image/jpeg',
                      100
                    );
                  },
                  {
                    orientation: true,
                    canvas: true,
                    crossOrigin: 'anonymous' // 👈 crucial line!
                  }
                );
              });
              setImageBlob(processedFile)
              setImgSrc(URL.createObjectURL(processedFile));
            }

          }}
        />

    }

    return <Image src={defaultPhotoPreview} sx={{
      objectFit: 'contain !important',
      objectPosition: 'center',
      borderRadius: '50%',
    }} width={props?.width || 150} minWidth={"100%"} height={150} />
  }

  return (
    <>
      <AvatarPreviewWrap {...props}>
        <Flex flexDirection="column" width="100%" alignItems="center" color="white" >
          <Text color="#0E0E0E" fontFamily="Poppins" fontSize="18px" lineHeight="165%" letterSpacing="0.18px">
            {window?.location?.search.includes('?step=10') ? 'Your Profile Photo' : ""}
          </Text>
          {RenderImage()}
          <Flex width="100%" mt={2} mb={1} alignItems="center" justifyContent="center">
            {!!imgSrc && <Flex justifyContent="center" alignItems="center" mr={3} onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEditPress(imgSrc, imageBlob)
            }}>
              <FiEdit color="#9B59B6" size={24} />
              <Text ml="4px" color="#9B59B6" fontFamily="Roboto" fontSize="16px" fontWeight={[500, 400]} lineHeight="150%" >Edit</Text>
            </Flex>}

            <Flex justifyContent="center" alignItems="center">
              <FiImage color="#9B59B6" size={24} />
              <Text ml="4px" color="#9B59B6" fontFamily="Roboto" fontSize="16px" fontWeight={[500, 400]} lineHeight="150%" >Update Photo</Text>
            </Flex>
          </Flex>
        </Flex>
      </AvatarPreviewWrap >
    </>
  );
};

AvatarPreviewImage.propTypes = {
  src: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(File),
    PropTypes.instanceOf(Blob),
  ]),
  circular: PropTypes.bool.isRequired,
  iconSize: PropTypes.number,
  hasEditButton: PropTypes.bool.isRequired,
};

AvatarPreviewImage.defaultProps = {
  src: '',
  iconSize: 20,
};

export default AvatarPreviewImage;
